import React, { useState, useEffect, useRef } from "react";
import { useDragSort } from "../lib/useDragSort";
import { CustomDomainField } from "../components/CustomDomainField";
import { GLOWS } from "../components/LogoRevealBg";
import WelcomeSplash, { splashMedia } from "../components/WelcomeSplash";
import SplashSettings from "../components/SplashSettings";
import { primeAudio } from "../lib/whoosh";
import { useParams, useNavigate } from "react-router-dom";
import { usePortfolios } from "../context/PortfolioContext";
import PortfolioBackground from "../components/PortfolioBackground";
import { Block, ProfileHeader } from "../components/PortfolioRenderer";
import { CoverHeader } from "../components/CoverHeader";
import BlockEditor from "../components/BlockEditor";
import { BLOCK_LIBRARY, BLOCK_CATEGORIES, newBlock, uid, AVATAR_FRAMES } from "../mock";
import {
  ArrowLeft,
  ExternalLink,
  ChevronUp,
  GripVertical,
  ChevronDown,
  Trash2,
  Copy,
  Plus,
  Check,
  Ban,
  CheckCircle2,
  Pencil,
  Heading,
  Type,
  MousePointerClick,
  Share2,
  MapPin,
  LayoutGrid,
  LayoutPanelTop,
  Images,
  GalleryHorizontal,
  GalleryHorizontalEnd,
  CreditCard,
  Image as ImageIcon,
  Video,
  Waves,
  Sparkles,
  Upload,
  BadgeCheck,
  Star,
  Crown,
  Heart,
  Award,
  Youtube,
  Minus,
  UserPlus,
  ListChecks,
  Timer,
  GitCommitVertical,
  Music,
  Circle,
  QrCode,
  Image,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { toast } from "../hooks/use-toast";
import { apiUploadFile } from "../api";
import { captureVideoPosterBlob, blobToFile } from "../lib/videoPoster";
import { copyToClipboard } from "../lib/clipboard";

const ICONS = { Heading, Type, MousePointerClick, Share2, MapPin, LayoutGrid, LayoutPanelTop, Images, Youtube, Minus, UserPlus, Star, ListChecks, Timer, GitCommitVertical, Music, Circle, QrCode, Image, GalleryHorizontal, GalleryHorizontalEnd, CreditCard };

const BLOCK_TITLES = {
  heading: "Título",
  text: "Texto",
  button: "Botón",
  socials: "Redes sociales",
  location: "Ubicación",
  products: "Carrusel de productos",
  showcase: "Vitrina de productos/servicios",
  showcase_strip: "Vitrina deslizable",
  divider: "Divisor",
  gallery: "Galería",
  mosaic: "Galería mosaico",
  flyers: "Flayers 360",
  mosaic_h: "Mosaico horizontal",
  pay: "Pagos con tarjeta",
  youtube: "Video de YouTube",
};

const MAX_BG_MB = 200;

const linkSanitize = (str) =>
  (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-/, "");

// Inline "add block" divider with a library popover
const AddDivider = ({ onAdd, big = false, kind }) => {
  const [open, setOpen] = useState(false);
  const library = BLOCK_LIBRARY.filter(
    (item) =>
      !((item.type === "showcase" || item.type === "showcase_strip") && kind === "card")
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`group flex w-full items-center justify-center gap-2 rounded-xl border border-dashed transition ${
            big
              ? "border-blue-400/60 bg-blue-500/10 py-4 text-blue-200 hover:bg-blue-500/20"
              : "border-white/20 py-2 text-white/40 hover:border-blue-400/60 hover:text-blue-200"
          }`}
        >
          <Plus className="h-4 w-4" />
          <span className="text-xs font-semibold tracking-wide">
            {big ? "AGREGAR NUEVO BLOQUE" : "Agregar aquí"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="center">
        <p className="px-2 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Elige un formato
        </p>
        <div className="grid max-h-[46vh] gap-1 overflow-y-auto overscroll-contain pr-1">
          {BLOCK_CATEGORIES.map((cat) => {
            const items = library.filter((it) => it.category === cat);
            if (items.length === 0) return null;
            return (
              <div key={cat} className="mb-1">
                <p className="sticky top-0 z-10 bg-white px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-widest text-blue-500/80">
                  {cat}
                </p>
                {items.map((item) => {
                  const Icon = ICONS[item.icon] || Type;
                  return (
                    <button
                      key={item.type}
                      onClick={() => {
                        onAdd(item.type);
                        setOpen(false);
                      }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-blue-500" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// A block wrapped with hover controls
const EditableBlock = ({ block, index, total, onEdit, onDelete, onMove, onDuplicate, itemProps, handleProps }) => (
  <div {...itemProps} className="ds-item group relative rounded-2xl outline-2 outline-transparent transition hover:outline-dashed hover:outline-blue-400/50" data-testid={`editable-block-${index}`}>
    <div className="absolute -top-3 right-2 z-30 flex items-center gap-0.5 rounded-full bg-white p-1 opacity-0 shadow-lg transition pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100">
      <button
        {...handleProps}
        title="Mantén presionado y arrastra"
        className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 active:bg-blue-100 active:text-blue-600"
        data-testid={`drag-handle-${index}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <button
        onClick={onEdit}
        className="flex items-center gap-1 rounded-full bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700"
      >
        <Pencil className="h-3.5 w-3.5" /> Editar
      </button>
      <button
        onClick={() => onMove(-1)}
        disabled={index === 0}
        className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
      >
        <ChevronUp className="h-4 w-4" />
      </button>
      <button
        onClick={() => onMove(1)}
        disabled={index === total - 1}
        className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
      >
        <ChevronDown className="h-4 w-4" />
      </button>
      <button
        onClick={onDuplicate}
        title="Duplicar"
        className="rounded-full p-1.5 text-blue-600 hover:bg-blue-50"
      >
        <Copy className="h-4 w-4" />
      </button>
      <button
        onClick={onDelete}
        className="rounded-full p-1.5 text-red-500 hover:bg-red-50"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
    <div className="pointer-events-none">
      <div className="pointer-events-auto">
        <Block block={block} editable />
      </div>
    </div>
  </div>
);

const Editor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getPortfolio, updatePortfolio, flushNow, toggleStatus, ready } = usePortfolios();

  const [draft, setDraft] = useState(null);
  const blocksRef = useRef(null);
  const { handleProps, itemProps } = useDragSort(blocksRef, (from, to) =>
    setDraft((d) => {
      const blocks = [...d.blocks];
      const [b] = blocks.splice(from, 1);
      blocks.splice(to, 0, b);
      return { ...d, blocks };
    })
  );
  const [splashPreview, setSplashPreview] = useState(null);
  const [editing, setEditing] = useState(null); // {kind:'profile'} | {kind:'block', id}
  const [uploadUI, setUploadUI] = useState({ active: false, target: null, pct: 0, thumb: "", kind: "image" });

  useEffect(() => {
    if (ready) {
      const p = getPortfolio(id);
      if (p) setDraft(JSON.parse(JSON.stringify(p)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, id]);

  useEffect(() => {
    if (draft) updatePortfolio(draft.id, () => draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Guarda de inmediato al salir de la página (evita perder los últimos cambios).
  useEffect(() => {
    const flush = () => {
      if (draft) flushNow(draft.id);
    };
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Retro-genera la captura (poster) de fondos/portadas de video que aún no la tengan,
  // para que la vista previa al compartir muestre una imagen del video.
  const posterCheckedRef = useRef(false);
  useEffect(() => {
    if (!draft || posterCheckedRef.current) return;
    posterCheckedRef.current = true;
    (async () => {
      const bg = draft.background;
      if (bg && bg.type === "video" && bg.url && !bg.poster) {
        try {
          const blob = await captureVideoPosterBlob(bg.url);
          const up = await apiUploadFile(blobToFile(blob, "fondo-poster.jpg"));
          setDraft((d) => ({ ...d, background: { ...d.background, poster: up.url } }));
        } catch (err) {}
      }
      const cov = draft.cover;
      if (cov && cov.type === "video" && cov.url && !cov.poster) {
        try {
          const blob = await captureVideoPosterBlob(cov.url);
          const up = await apiUploadFile(blobToFile(blob, "portada-poster.jpg"));
          setDraft((d) => ({ ...d, cover: { ...d.cover, poster: up.url } }));
        } catch (err) {}
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a1633] text-white/50">
        Cargando editor...
      </div>
    );
  }

  const setProfile = (patch) =>
    setDraft((d) => ({ ...d, profile: { ...d.profile, ...patch } }));

  const setCover = (patch) =>
    setDraft((d) => ({ ...d, cover: { ...(d.cover || { type: "image", url: "", position: 50 }), ...patch } }));

  const runUpload = async (file, target, apply) => {
    const localUrl = URL.createObjectURL(file);
    setUploadUI({ active: true, target, pct: 0, thumb: localUrl, kind: file.type.startsWith("video") ? "video" : "image" });
    try {
      const { url } = await apiUploadFile(file, (p) => setUploadUI((s) => ({ ...s, pct: p })));
      await apply(url, file);
    } catch (err) {
      toast({ title: "Error al subir", description: "Intenta de nuevo." });
    } finally {
      setTimeout(() => {
        setUploadUI({ active: false, target: null, pct: 0, thumb: "", kind: "image" });
        URL.revokeObjectURL(localUrl);
      }, 500);
    }
  };

  const UploadBar = (target) => {
    if (!uploadUI.active || uploadUI.target !== target) return null;
    return (
      <div className="flex items-center gap-3 rounded-xl border-2 border-blue-200 bg-blue-50/60 px-3 py-2" data-testid="editor-upload-progress">
        {uploadUI.kind === "video" ? (
          <video src={uploadUI.thumb} muted playsInline className="h-11 w-11 shrink-0 rounded-md object-cover" />
        ) : (
          <img src={uploadUI.thumb} alt="" className="h-11 w-11 shrink-0 rounded-md object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between text-[11px] font-medium">
            <span className="truncate text-slate-600">Subiendo {uploadUI.kind === "video" ? "video" : "foto"}…</span>
            <span className="shrink-0 font-semibold text-green-600">{uploadUI.pct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full rounded-full bg-green-500 transition-all duration-200" style={{ width: `${uploadUI.pct}%` }} />
          </div>
        </div>
      </div>
    );
  };

  const onPickCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BG_MB * 1024 * 1024) {
      toast({ title: "Archivo demasiado grande", description: `El tamaño máximo es ${MAX_BG_MB} MB.` });
      e.target.value = "";
      return;
    }
    e.target.value = "";
    const isVideo = file.type.startsWith("video");
    await runUpload(file, "cover", async (url) => {
      setCover({ type: isVideo ? "video" : "image", url, poster: "" });
      if (isVideo) {
        try {
          const blob = await captureVideoPosterBlob(file);
          const up = await apiUploadFile(blobToFile(blob, "portada-poster.jpg"));
          setCover({ poster: up.url });
        } catch (perr) {}
      }
    });
  };

  const setBackground = (patch) =>
    setDraft((d) => ({
      ...d,
      background: { ...(d.background || { type: "aurora", url: "" }), ...patch },
    }));

  const onPickBgFile = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BG_MB * 1024 * 1024) {
      toast({ title: "Archivo demasiado grande", description: `El tamaño máximo es ${MAX_BG_MB} MB.` });
      e.target.value = "";
      return;
    }
    e.target.value = "";
    await runUpload(file, "bg", async (url) => {
      setBackground({ type: kind, url, poster: "" });
      if (kind === "video") {
        try {
          const blob = await captureVideoPosterBlob(file);
          const up = await apiUploadFile(blobToFile(blob, "fondo-poster.jpg"));
          setBackground({ poster: up.url });
        } catch (perr) {}
      }
    });
  };

  const onPickAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BG_MB * 1024 * 1024) {
      toast({ title: "Archivo demasiado grande", description: `El tamaño máximo es ${MAX_BG_MB} MB.` });
      e.target.value = "";
      return;
    }
    e.target.value = "";
    const isVideo = file.type.startsWith("video");
    await runUpload(file, "avatar", async (url) => {
      setProfile({ avatar: url, avatarType: isVideo ? "video" : "image" });
    });
  };

  const setBlock = (blockId, updated) =>
    setDraft((d) => ({
      ...d,
      blocks: d.blocks.map((b) => (b.id === blockId ? updated : b)),
    }));


  const moveBlock = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= draft.blocks.length) return;
    const blocks = [...draft.blocks];
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    setDraft((d) => ({ ...d, blocks }));
  };

  const deleteBlock = (blockId) => {
    setDraft((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== blockId) }));
    toast({ title: "Bloque eliminado" });
  };

  const duplicateBlock = (index) => {
    setDraft((d) => {
      const clone = JSON.parse(JSON.stringify(d.blocks[index]));
      clone.id = uid();
      if (Array.isArray(clone.items)) {
        clone.items = clone.items.map((it) => ({ ...it, id: uid() }));
      }
      const blocks = [...d.blocks];
      blocks.splice(index + 1, 0, clone);
      return { ...d, blocks };
    });
    toast({ title: "Bloque duplicado", description: "Copia agregada debajo." });
  };

  const insertBlock = (index, type) => {
    const b = newBlock(type);
    setDraft((d) => {
      const blocks = [...d.blocks];
      blocks.splice(index, 0, b);
      return { ...d, blocks };
    });
    setEditing({ kind: "block", id: b.id });
    toast({ title: "Bloque agregado", description: "Personalízalo ahora." });
  };

  const editingBlock =
    editing?.kind === "block" ? draft.blocks.find((b) => b.id === editing.id) : null;

  return (
    <div className="min-h-screen bg-[#0a1633]">
      {/* Header */}
      <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-white/10 bg-[#0a1633]/90 px-4 py-3 backdrop-blur">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10 hover:text-white"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <p className="truncate font-display text-base font-bold text-white">
              {draft.name}
            </p>
            <p className="flex items-center gap-1 text-xs text-emerald-400">
              <Check className="h-3 w-3" /> Guardado automáticamente
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="gap-1.5 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
            onClick={() =>
              setDraft((d) => {
                toggleStatus(d.id);
                return { ...d, status: d.status === "active" ? "suspended" : "active" };
              })
            }
          >
            {draft.status === "active" ? (
              <>
                <Ban className="h-4 w-4 text-amber-400" /> Suspender
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Reactivar
              </>
            )}
          </Button>
          <Button
            className="gap-1.5 bg-blue-600 hover:bg-blue-700"
            onClick={() => window.open(`/${draft.domain || "uaicard"}/${draft.slug}`, "_blank")}
          >
            <ExternalLink className="h-4 w-4" /> Ver publicado
          </Button>
        </div>
      </header>

      {/* Live editable portfolio */}
      <div className="relative min-h-[calc(100vh-61px)]">
        <PortfolioBackground background={draft.background} />

        {/* Change-background floating button (top-right, where requested) */}
        <button
          onClick={() => setEditing({ kind: "background" })}
          className="absolute right-4 top-4 z-30 flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-semibold text-white backdrop-blur transition hover:bg-white/20"
        >
          <ImageIcon className="h-4 w-4" /> Cambiar fondo
        </button>
        <button
          onClick={() => setEditing({ kind: "splash" })}
          className="absolute right-4 top-16 z-30 flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-semibold text-white backdrop-blur transition hover:bg-white/20"
          data-testid="open-splash-settings"
        >
          <Sparkles className="h-4 w-4" /> Bienvenida
        </button>
        {splashPreview && (
          <WelcomeSplash
            {...splashMedia(draft)}
            glow={draft.background?.glow}
            sound={!!draft.splashSound}
            exiting={splashPreview === "exit"}
          />
        )}

        <div ref={blocksRef} className="relative z-10 mx-auto flex w-full max-w-md flex-col gap-4 px-5 pb-28 pt-8">
          {/* Editable profile */}
          <button
            onClick={() => setEditing({ kind: "profile" })}
            className="group relative rounded-2xl p-2 outline-2 outline-transparent transition hover:outline-dashed hover:outline-blue-400/50"
          >
            <span className="absolute right-2 top-2 z-30 flex items-center gap-1 rounded-full bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100">
              <Pencil className="h-3.5 w-3.5" /> Editar perfil
            </span>
            {draft.kind === "card" ? (
              <CoverHeader portfolio={draft} />
            ) : (
              <ProfileHeader profile={draft.profile} />
            )}
          </button>

          {/* Blocks with inline add dividers */}
          {draft.blocks.map((block, i) => (
            <React.Fragment key={block.id}>
              <AddDivider onAdd={(type) => insertBlock(i, type)} kind={draft.kind} />
              <EditableBlock
                block={block}
                index={i}
                total={draft.blocks.length}
                onEdit={() => setEditing({ kind: "block", id: block.id })}
                onDelete={() => deleteBlock(block.id)}
                onMove={(dir) => moveBlock(i, dir)}
                onDuplicate={() => duplicateBlock(i)}
                itemProps={itemProps(i)}
                handleProps={handleProps(i)}
              />
            </React.Fragment>
          ))}

          <AddDivider onAdd={(type) => insertBlock(draft.blocks.length, type)} big kind={draft.kind} />

          <div className="mt-2 flex flex-col items-center gap-1">
            <span className="text-[11px] text-white/40">Creado con</span>
            <span className="font-display text-sm font-bold tracking-wide text-white/80">
              UAICARD <span className="text-blue-400">BUSINESS</span>
            </span>
          </div>
        </div>
      </div>

      {/* Edit drawer */}
      <Sheet
        open={!!editing}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            if (draft) flushNow(draft.id);
          }
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>
              {editing?.kind === "profile"
                ? "Editar perfil"
                : editing?.kind === "background"
                ? "Cambiar fondo de pantalla"
                : editing?.kind === "splash"
                ? "Pantalla de bienvenida"
                : `Editar: ${editingBlock ? BLOCK_TITLES[editingBlock.type] : ""}`}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6">
            {editing?.kind === "splash" && (
              <SplashSettings
                draft={draft}
                setDraft={setDraft}
                setBackground={setBackground}
                onPreview={() => {
                  setEditing(null);
                  if (draft.splashSound) primeAudio();
                  setSplashPreview("show");
                  setTimeout(() => setSplashPreview("exit"), 2000);
                  setTimeout(() => setSplashPreview(null), 2700);
                }}
              />
            )}

            {editing?.kind === "background" && (
              <div className="space-y-5">
                <div>
                  <Label className="mb-2 block text-xs font-medium text-slate-500">
                    Tipo de fondo
                  </Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { key: "aurora", label: "Ondas", icon: Waves },
                      { key: "logo3d", label: "Logo 3D", icon: Sparkles },
                      { key: "image", label: "Imagen", icon: ImageIcon },
                      { key: "video", label: "Video", icon: Video },
                    ].map((opt) => {
                      const active = (draft.background?.type || "aurora") === opt.key;
                      const OptIcon = opt.icon;
                      return (
                        <button
                          key={opt.key}
                          onClick={() => setBackground({ type: opt.key })}
                          className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-xs font-semibold transition ${
                            active
                              ? "border-blue-600 bg-blue-50 text-blue-700"
                              : "border-slate-200 text-slate-600 hover:border-blue-300"
                          }`}
                        >
                          <OptIcon className="h-5 w-5" />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {draft.background?.type === "logo3d" && (
                  <div className="space-y-2 rounded-xl bg-slate-50 p-3" data-testid="glow-color">
                    <p className="text-xs font-semibold text-slate-500">Color del destello del logo</p>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(GLOWS).map(([k, g]) => {
                        const on = (draft.background?.glow || "blue") === k;
                        return (
                          <button
                            key={k}
                            type="button"
                            onClick={() => setBackground({ glow: k })}
                            className={`flex items-center justify-center gap-2 rounded-lg border-2 py-2 text-xs font-semibold transition ${
                              on ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-blue-300"
                            }`}
                            data-testid={`glow-${k}`}
                          >
                            <span className="h-4 w-4 rounded-full border border-slate-300" style={{ background: g.swatch, boxShadow: `0 0 8px ${g.swatch}` }} />
                            {g.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {draft.background?.type === "logo3d" && (
                  <div className="space-y-2 rounded-xl bg-slate-50 p-3" data-testid="logo3d-options">
                    <p className="text-xs text-slate-500">
                      Fondo negro con el logo en 3D y destellos que van mostrando partes del logo.
                    </p>
                    <div className="flex items-center gap-3">
                      <span className="flex h-14 w-20 items-center justify-center rounded-lg bg-black p-1.5">
                        <img src={draft.background.logo || "/logo-symbol-light.png"} alt="" className="max-h-full max-w-full object-contain" />
                      </span>
                      <label className="cursor-pointer rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700">
                        Subir mi logo (PNG sin fondo)
                        <input
                          type="file"
                          accept="image/png,image/webp,image/svg+xml"
                          className="hidden"
                          data-testid="logo3d-upload"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            try {
                              const { url } = await apiUploadFile(f);
                              setBackground({ logo: url });
                            } catch (err) {
                              toast({ title: "No se pudo subir el logo", variant: "destructive" });
                            }
                          }}
                        />
                      </label>
                      {draft.background.logo && (
                        <button className="text-xs font-semibold text-slate-500 hover:text-red-500" onClick={() => setBackground({ logo: "" })}>
                          Usar logo UAICARD
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {(draft.background?.type === "image" ||
                  draft.background?.type === "video") && (
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-500">
                        {draft.background.type === "video"
                          ? "URL del video (mp4/webm)"
                          : "URL de la imagen"}
                      </Label>
                      <Input
                        value={draft.background.url || ""}
                        placeholder="https://..."
                        onChange={(e) => setBackground({ url: e.target.value })}
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="h-px flex-1 bg-slate-200" />
                      <span className="text-xs text-slate-400">o</span>
                      <span className="h-px flex-1 bg-slate-200" />
                    </div>

                    <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-4 text-sm font-medium text-slate-600 transition hover:border-blue-400 hover:text-blue-600">
                      <Upload className="h-4 w-4" />
                      Subir {draft.background.type === "video" ? "video" : "imagen"} (máx. {MAX_BG_MB} MB)
                      <input
                        type="file"
                        accept={draft.background.type === "video" ? "video/*" : "image/*"}
                        className="hidden"
                        onChange={(e) => onPickBgFile(e, draft.background.type)}
                      />
                    </label>
                    {UploadBar("bg")}

                    {draft.background.url && (
                      <div className="overflow-hidden rounded-xl border border-slate-200">
                        {draft.background.type === "video" ? (
                          <video
                            src={draft.background.url}
                            className="h-40 w-full object-cover"
                            muted
                            loop
                            autoPlay
                            playsInline
                          />
                        ) : (
                          <img
                            src={draft.background.url}
                            alt=""
                            className="h-40 w-full object-cover"
                          />
                        )}
                      </div>
                    )}

                    <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                      El video se reproduce en bucle y sin sonido. Los archivos
                      se guardan en el servidor (máx. {MAX_BG_MB} MB) y se ven en
                      cualquier dispositivo.
                    </p>
                  </div>
                )}
              </div>
            )}

            {editing?.kind === "profile" && (
              <div className="space-y-4">
                {draft.kind === "card" && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-slate-500">
                      Portada (imagen o video horizontal)
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: "image", label: "Imagen", icon: ImageIcon },
                        { key: "video", label: "Video", icon: Video },
                      ].map((opt) => {
                        const active = (draft.cover?.type || "image") === opt.key;
                        const OptIcon = opt.icon;
                        return (
                          <button
                            key={opt.key}
                            onClick={() => setCover({ type: opt.key })}
                            className={`flex items-center justify-center gap-1.5 rounded-xl border-2 py-2 text-xs font-semibold transition ${
                              active
                                ? "border-blue-600 bg-blue-50 text-blue-700"
                                : "border-slate-200 text-slate-600 hover:border-blue-300"
                            }`}
                          >
                            <OptIcon className="h-4 w-4" />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <Input
                      value={draft.cover?.url || ""}
                      onChange={(e) => setCover({ url: e.target.value })}
                      placeholder={
                        (draft.cover?.type || "image") === "video"
                          ? "URL del video (mp4/webm)"
                          : "URL de la imagen"
                      }
                    />
                    <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-medium text-slate-600 transition hover:border-blue-400 hover:text-blue-600">
                      <Upload className="h-4 w-4" />
                      Subir {(draft.cover?.type || "image") === "video" ? "video" : "imagen"} (máx. {MAX_BG_MB} MB)
                      <input
                        type="file"
                        accept={(draft.cover?.type || "image") === "video" ? "video/*" : "image/*"}
                        className="hidden"
                        onChange={onPickCoverFile}
                      />
                    </label>
                    {UploadBar("cover")}
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-slate-500">
                        Centrar / recortar (posición vertical)
                      </Label>
                      <span className="text-xs font-semibold text-blue-600">
                        {typeof draft.cover?.position === "number" ? draft.cover.position : 50}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={typeof draft.cover?.position === "number" ? draft.cover.position : 50}
                      onChange={(e) => setCover({ position: Number(e.target.value) })}
                      className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                    />
                    <p className="text-[11px] text-slate-400">
                      Portada rectangular horizontal. El video se reproduce en
                      bucle, sin sonido.
                    </p>
                  </div>
                )}
                {draft.kind !== "card" && (
                <>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-slate-500">
                    Foto / video de perfil
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "image", label: "Imagen", icon: ImageIcon },
                      { key: "video", label: "Video", icon: Video },
                    ].map((opt) => {
                      const active = (draft.profile.avatarType || "image") === opt.key;
                      const OptIcon = opt.icon;
                      return (
                        <button
                          key={opt.key}
                          onClick={() => setProfile({ avatarType: opt.key })}
                          className={`flex items-center justify-center gap-1.5 rounded-xl border-2 py-2 text-xs font-semibold transition ${
                            active
                              ? "border-blue-600 bg-blue-50 text-blue-700"
                              : "border-slate-200 text-slate-600 hover:border-blue-300"
                          }`}
                        >
                          <OptIcon className="h-4 w-4" />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <Input
                    value={draft.profile.avatar}
                    onChange={(e) => setProfile({ avatar: e.target.value })}
                    placeholder={
                      (draft.profile.avatarType || "image") === "video"
                        ? "URL del video (mp4/webm)"
                        : "URL de la imagen"
                    }
                  />
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-medium text-slate-600 transition hover:border-blue-400 hover:text-blue-600">
                    <Upload className="h-4 w-4" />
                    Subir {(draft.profile.avatarType || "image") === "video" ? "video" : "foto"} (máx. {MAX_BG_MB} MB)
                    <input
                      type="file"
                      accept={
                        (draft.profile.avatarType || "image") === "video"
                          ? "video/*"
                          : "image/*"
                      }
                      className="hidden"
                      onChange={onPickAvatarFile}
                    />
                  </label>
                  {UploadBar("avatar")}
                  <p className="text-[11px] text-slate-400">
                    Se muestra en forma de esfera (círculo). El video se
                    reproduce en bucle, sin sonido.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-slate-500">
                      Tamaño de la foto
                    </Label>
                    <span className="text-xs font-semibold text-blue-600">
                      {draft.profile.avatarSize || 112}px
                    </span>
                  </div>
                  <input
                    type="range"
                    min="72"
                    max="200"
                    step="2"
                    value={draft.profile.avatarSize || 112}
                    onChange={(e) =>
                      setProfile({ avatarSize: Number(e.target.value) })
                    }
                    className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                  />
                </div>

                <div className="space-y-2 rounded-xl bg-slate-50 p-3">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Marco de la foto (animado)
                  </Label>
                  <div className="grid grid-cols-5 gap-2">
                    {AVATAR_FRAMES.map((f) => {
                      const active = (draft.profile.avatarFrame || "none") === f.key;
                      return (
                        <button
                          key={f.key}
                          type="button"
                          onClick={() => setProfile({ avatarFrame: f.key })}
                          data-testid={`avatar-frame-${f.key}`}
                          className={`flex flex-col items-center gap-1 rounded-lg border-2 p-1.5 transition ${
                            active
                              ? "border-blue-600 bg-blue-50"
                              : "border-transparent hover:border-blue-300"
                          }`}
                        >
                          {f.key === "none" ? (
                            <span className="h-9 w-9 rounded-full border-2 border-slate-300 bg-white" />
                          ) : (
                            <span
                              className={`pf-frame pf-fr-${f.key} block h-9 w-9`}
                              style={
                                f.key === "manual"
                                  ? { "--pf-color": draft.profile.avatarFrameColor || "#2E6BFF" }
                                  : undefined
                              }
                            >
                              <span className="pf-frame-inner block h-full w-full bg-white" />
                            </span>
                          )}
                          <span className="text-[9px] leading-none text-slate-500">{f.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {draft.profile.avatarFrame === "manual" && (
                    <div className="flex items-center justify-between pt-1">
                      <Label className="text-xs font-medium text-slate-500">Color del marco</Label>
                      <input
                        type="color"
                        value={draft.profile.avatarFrameColor || "#2E6BFF"}
                        onChange={(e) => setProfile({ avatarFrameColor: e.target.value })}
                        className="h-9 w-14 cursor-pointer rounded border border-slate-200 bg-white"
                        data-testid="avatar-frame-color"
                      />
                    </div>
                  )}
                </div>

                {draft.profile.avatar && (
                  <div className="space-y-3 rounded-xl bg-slate-50 p-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Centrar la foto en el círculo
                      </Label>
                      <button
                        type="button"
                        onClick={() => setProfile({ avatarPosX: 50, avatarPosY: 50, avatarZoom: 1 })}
                        className="text-xs text-blue-600 underline"
                        data-testid="avatar-center-reset"
                      >
                        Centrar
                      </button>
                    </div>
                    {/* Vista previa del recorte circular */}
                    <div className="flex justify-center">
                      <div className="h-24 w-24 overflow-hidden rounded-full border-4 border-white shadow-md">
                        {(draft.profile.avatarType || "image") === "video" ? (
                          <video
                            src={draft.profile.avatar}
                            className="h-full w-full object-cover"
                            style={{
                              objectPosition: `${draft.profile.avatarPosX ?? 50}% ${draft.profile.avatarPosY ?? 50}%`,
                              transform: `scale(${draft.profile.avatarZoom ?? 1})`,
                              transformOrigin: `${draft.profile.avatarPosX ?? 50}% ${draft.profile.avatarPosY ?? 50}%`,
                            }}
                            muted
                            playsInline
                            autoPlay
                            loop
                          />
                        ) : (
                          <img
                            src={draft.profile.avatar}
                            alt="preview"
                            className="h-full w-full object-cover"
                            style={{
                              objectPosition: `${draft.profile.avatarPosX ?? 50}% ${draft.profile.avatarPosY ?? 50}%`,
                              transform: `scale(${draft.profile.avatarZoom ?? 1})`,
                              transformOrigin: `${draft.profile.avatarPosX ?? 50}% ${draft.profile.avatarPosY ?? 50}%`,
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-500">
                        Horizontal (izquierda ↔ derecha)
                      </Label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={draft.profile.avatarPosX ?? 50}
                        onChange={(e) => setProfile({ avatarPosX: Number(e.target.value) })}
                        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                        data-testid="avatar-pos-x"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-500">
                        Vertical (arriba ↕ abajo)
                      </Label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={draft.profile.avatarPosY ?? 50}
                        onChange={(e) => setProfile({ avatarPosY: Number(e.target.value) })}
                        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                        data-testid="avatar-pos-y"
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium text-slate-500">
                          Zoom (acercar / alejar)
                        </Label>
                        <span className="text-xs font-semibold text-blue-600">
                          {(draft.profile.avatarZoom ?? 1).toFixed(1)}x
                        </span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="3"
                        step="0.1"
                        value={draft.profile.avatarZoom ?? 1}
                        onChange={(e) => setProfile({ avatarZoom: Number(e.target.value) })}
                        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                        data-testid="avatar-zoom"
                      />
                    </div>
                  </div>
                )}
                </>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-500">
                    Nombre del negocio
                  </Label>
                  <Input
                    value={draft.profile.businessName}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        name: e.target.value,
                        profile: { ...d.profile, businessName: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-500">Subtítulo</Label>
                  <Textarea
                    rows={2}
                    value={draft.profile.subtitle}
                    onChange={(e) => setProfile({ subtitle: e.target.value })}
                  />
                </div>
                <div className="space-y-3 rounded-lg bg-slate-50 px-3 py-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-slate-600">Mostrar insignia</Label>
                    <button
                      onClick={() => setProfile({ verified: !draft.profile.verified })}
                      className={`relative h-6 w-11 rounded-full transition ${
                        draft.profile.verified ? "bg-blue-600" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                          draft.profile.verified ? "left-[22px]" : "left-0.5"
                        }`}
                      />
                    </button>
                  </div>
                  {draft.profile.verified && (
                    <div>
                      <Label className="mb-2 block text-xs font-medium text-slate-500">
                        Diseño de insignia
                      </Label>
                      <div className="flex gap-2">
                        {[
                          { key: "check", bg: "bg-blue-500", Icon: BadgeCheck },
                          { key: "star", bg: "bg-amber-500", Icon: Star },
                          { key: "crown", bg: "bg-yellow-500", Icon: Crown },
                          { key: "heart", bg: "bg-rose-500", Icon: Heart },
                          { key: "award", bg: "bg-emerald-500", Icon: Award },
                        ].map(({ key, bg, Icon }) => {
                          const active = (draft.profile.badgeStyle || "check") === key;
                          return (
                            <button
                              key={key}
                              onClick={() => setProfile({ badgeStyle: key })}
                              className={`flex h-11 w-11 items-center justify-center rounded-xl border-2 transition ${
                                active
                                  ? "border-blue-600 bg-blue-50"
                                  : "border-slate-200 hover:border-blue-300"
                              }`}
                            >
                              <span className={`flex h-7 w-7 items-center justify-center rounded-full ${bg}`}>
                                <Icon className="h-4 w-4 text-white" />
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-500">Fecha de inicio</Label>
                  <Input
                    type="date"
                    value={draft.startDate}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, startDate: e.target.value }))
                    }
                  />
                </div>

                <div className="space-y-2 rounded-lg bg-slate-50 p-3">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Dirección web (enlace a compartir)
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[11px] text-slate-400">Dominio</Label>
                      <Input
                        value={draft.domain || "uaicard"}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, domain: linkSanitize(e.target.value) }))
                        }
                        placeholder="uaicard"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-slate-400">Subdominio</Label>
                      <Input
                        value={draft.slug}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, slug: linkSanitize(e.target.value) }))
                        }
                        placeholder="mi-negocio"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="flex-1 truncate rounded-md bg-white px-2 py-1.5 text-xs text-slate-600">
                      /{draft.domain || "uaicard"}/{draft.slug}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const full = `${window.location.origin}/api/v/${draft.slug}?t=${Date.now()}`;
                        copyToClipboard(full);
                        toast({ title: "Enlace copiado", description: full });
                      }}
                      className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                    >
                      Copiar
                    </button>
                  </div>
                </div>

                <CustomDomainField
                  value={draft.customDomain || ""}
                  onChange={(v) => setDraft((d) => ({ ...d, customDomain: v }))}
                  targetUrl={`${window.location.origin}/${draft.domain || "uaicard"}/${draft.slug}`}
                  onCopy={(u) => {
                    copyToClipboard(u);
                    toast({ title: "Destino copiado", description: u });
                  }}
                />

                <div className="space-y-2 rounded-lg bg-emerald-50/70 p-3" data-testid="thanks-box">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    Página de gracias (después de pagar con Stripe)
                  </Label>
                  <Input
                    value={draft.thanksWa || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, thanksWa: e.target.value.replace(/[^\d+]/g, "") }))}
                    placeholder="WhatsApp para confirmar (ej. 5215512345678)"
                    inputMode="tel"
                    data-testid="thanks-wa-input"
                  />
                  <Input
                    value={draft.thanksMsg || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, thanksMsg: e.target.value }))}
                    placeholder="Mensaje (ej. Hola, acabo de pagar mi pedido)"
                  />
                  <div className="flex items-center gap-2">
                    <p className="flex-1 truncate rounded-md bg-white px-2 py-1.5 text-xs text-slate-600">
                      {`${window.location.origin}/${draft.domain || "uaicard"}/${draft.slug}/gracias`}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const u = `${window.location.origin}/${draft.domain || "uaicard"}/${draft.slug}/gracias`;
                        copyToClipboard(u);
                        toast({ title: "Enlace de gracias copiado", description: u });
                      }}
                      className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                      data-testid="thanks-copy"
                    >
                      Copiar
                    </button>
                  </div>
                  <p className="text-[11px] leading-snug text-slate-500">
                    En Stripe, abre tu enlace de pago → <b>Después del pago</b> → <b>“No mostrar página de confirmación, redirigir a tu sitio web”</b> y pega este enlace. Si dejas vacío el WhatsApp, se usa el de tu primer botón de WhatsApp.
                  </p>
                </div>
              </div>
            )}

            {editingBlock && (
              <BlockEditor
                block={editingBlock}
                onChange={(u) => setBlock(editingBlock.id, u)}
              />
            )}
          </div>

          <div className="mt-8">
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => setEditing(null)}
            >
              Listo
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Editor;
